/**
 * When BusinessDayProvider reads GET /books/days (design 2026-10-06 section
 * 8.1) - the one read of the business day the whole Console shares. Pure:
 * the clock, the timers and the page's visibility are injected, so the rules
 * are tested without a browser (dayPoller.test.js), as auth/idle.js's are.
 *
 *   - the first read is made at once (the person has just signed in), unless
 *     the page is hidden;
 *   - after that, one read per heartbeat (auth/idle.js HEARTBEAT_MS, a
 *     minute), and only while the person was active within the last
 *     heartbeat window (businessDay.js pollGate): the authorizer counts every
 *     call as activity, so the poll never keeps an idle session alive - the
 *     idle monitor's own heartbeat already reaches the server at most once a
 *     minute while the person is active;
 *   - it stops once the idle warning would show, and while the page is
 *     hidden; activity (or the page shown again) wakes it, reading at once
 *     when a read is due;
 *   - a failed read backs off: two minutes, four, then five at most
 *     (pollDelay) - the firewall's per-session budget (500 calls in five
 *     minutes) is never the poll's to spend;
 *   - refresh() reads now, whatever the gate (an action just finished, or the
 *     person pressed Refresh); one asked for while a read is under way runs
 *     straight after it, so an answer older than the action never wins.
 */
import { HEARTBEAT_MS, IDLE_MS, WARN_MS } from '../auth/idle.js';
import { MAX_BACKOFF_MS, pollDelay, pollGate } from './businessDay.js';

/**
 * @param {{
 *   load: () => Promise<object>,                 GET /books/days
 *   onAnswer: (view: object) => void,
 *   onError?: (error: Error, info: { failures: number, retryInMs: number }) => void,
 *   isHidden?: () => boolean,
 *   now?: () => number, setTimer?: typeof setTimeout, clearTimer?: typeof clearTimeout,
 *   heartbeatMs?: number, idleMs?: number, warnMs?: number, maxBackoffMs?: number,
 * }} options
 */
export function createDayPoller({
  load, onAnswer, onError = () => {}, isHidden = () => false,
  now = () => Date.now(), setTimer = setTimeout, clearTimer = clearTimeout,
  heartbeatMs = HEARTBEAT_MS, idleMs = IDLE_MS, warnMs = WARN_MS, maxBackoffMs = MAX_BACKOFF_MS,
}) {
  let lastActivity = now();
  let lastRead = null;
  let failures = 0;
  let reading = false;
  let again = false;
  let timer = null;
  let stopped = false;

  const delay = () => pollDelay(failures, { heartbeatMs, maxMs: maxBackoffMs });
  const gateOpen = () => pollGate({ nowMs: now(), lastActivityMs: lastActivity, hidden: isHidden(), heartbeatMs, idleMs, warnMs }).open;

  function clear() {
    if (timer) clearTimer(timer);
    timer = null;
  }

  function arm(ms) {
    clear();
    if (!stopped) timer = setTimer(tick, Math.max(0, ms));
  }

  /** A read fell due: made when the gate is open; otherwise the poller sleeps until activity wakes it. */
  function tick() {
    timer = null;
    if (stopped || reading) return;
    if (gateOpen()) read();
  }

  /** Activity, or the page shown again: read now if a read is due, else keep (or set) the timer. */
  function wake() {
    if (stopped || reading || timer || !gateOpen()) return;
    const dueIn = lastRead === null ? 0 : lastRead + delay() - now();
    if (dueIn <= 0) read();
    else arm(dueIn);
  }

  function read() {
    clear();
    reading = true;
    Promise.resolve()
      .then(load)
      .then((answer) => {
        if (stopped) return;
        failures = 0;
        onAnswer(answer);
      }, (error) => {
        if (stopped) return;
        failures += 1;
        onError(error, { failures, retryInMs: delay() });
      })
      .finally(() => {
        reading = false;
        lastRead = now();
        if (stopped) return;
        if (again) {
          again = false;
          read();
        } else {
          arm(delay());
        }
      })
      // onAnswer and onError only set state; a throw there must not become an unhandled rejection.
      .catch(() => {});
  }

  return {
    /** The first read: at once, unless the page is hidden. */
    start() {
      wake();
    },
    /** Something the person did (auth/idle.js ACTIVITY_EVENTS). */
    activity() {
      lastActivity = now();
      wake();
    },
    /** The page was shown or hidden (visibilitychange). */
    visibility() {
      wake();
    },
    /** Read now: an action finished, or the person asked. */
    refresh() {
      if (stopped) return;
      if (reading) again = true;
      else read();
    },
    stop() {
      stopped = true;
      clear();
    },
  };
}
